"use client"

import { useState, useEffect } from "react"
import { useRef } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Box,
  Typography,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material"
import {
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  DeleteSweep as DeleteSweepIcon,
  Clear,
} from "@mui/icons-material"
import DashboardLayout from "../components/DashboardLayout"
import { countries } from "../data/countries"
import axios from "axios"
import { API_URL } from "../constants"
import { useSelector } from "react-redux" // Retain for userInfo


const WhiteListingPage = () => {
  const { userInfo } = useSelector((state) => state.auth)
  const navigate = useNavigate()
  const pollingRef = useRef(null)
  const location = useLocation()
  const isActiveRef = useRef(false)

  const [option, setOption] = useState("Domestic")
  const [country, setCountry] = useState("IN")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [numbers, setNumbers] = useState([])
  const [searchResults, setSearchResults] = useState([])
  const [pollingInterval, setPollingInterval] = useState(null)
  const [countryCode, setCountryCode] = useState("+91")
  const [filteredCountries, setFilteredCountries] = useState([])
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" })
  const [clearAllDialogOpen, setClearAllDialogOpen] = useState(false)
  const [hardDeleteDialogOpen, setHardDeleteDialogOpen] = useState(false)
  const [selectedNumberForHardDelete, setSelectedNumberForHardDelete] = useState(null)

  useEffect(() => {
    fetchNumbers()
    fetchSearchResults()
  }, [])

useEffect(() => {
  if (location.pathname === "/white-listing") {
    isActiveRef.current = true

    pollingRef.current = setInterval(() => {
      if (isActiveRef.current) {
        fetchSearchResults()
      }
    }, 3000)
  } else {
    isActiveRef.current = false
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }

  return () => {
    isActiveRef.current = false
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }
}, [location.pathname])

  const fetchNumbers = async () => {
    setIsLoading(true)
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.get(`${API_URL}/api/white-listing`, config)
      setNumbers(data)
    } catch (error) {
      if (error.response && error.response.status === 403) {
        showAlert("You do not have permission to access this resource", "error")
        setTimeout(() => {
          navigate("/dashboard")
        }, 2000)
      } else {
        showAlert(error.response?.data?.message || "Failed to fetch numbers", "error")
      }
    } finally {
      setIsLoading(false)
    }
  }

  const fetchSearchResults = async () => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.get(`${API_URL}/api/white-listing/search-results`, config)
      setSearchResults(data.results || [])
    } catch (error) {
      console.error("Failed to fetch search results:", error)
    }
  }

  const startPolling = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval)
    }

    const interval = setInterval(() => {
      fetchSearchResults()
    }, 3000)

    setPollingInterval(interval)
  }

  useEffect(() => {
    if (option === "Domestic") {
      setFilteredCountries(countries.filter((country) => country.code === "IN"))
      setCountry("IN")
    } else {
      const internationalCountries = countries.filter((country) => country.code !== "IN")
      setFilteredCountries(internationalCountries)

      if (internationalCountries.length > 0) {
        setCountry(internationalCountries[0].code)
      }
    }
  }, [option])

  useEffect(() => {
    const selectedCountry = countries.find((c) => c.code === country)
    if (selectedCountry) {
      setCountryCode(`+${selectedCountry.phone}`)
    }
  }, [country])

  const handleOptionChange = (event, newOption) => {
    if (newOption !== null) {
      setOption(newOption)
      setPhoneNumber("")
    }
  }

  const handleCountryChange = (event) => {
    setCountry(event.target.value)
    setPhoneNumber("")
  }

  const handlePhoneNumberChange = (event) => {
    const value = event.target.value.replace(/[^0-9]/g, "")
    setPhoneNumber(value)
  }

  const showAlert = (message, severity = "success") => {
    setAlert({ open: true, message, severity })
  }

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false })
  }

  const handleSearch = async () => {
    if (!validatePhoneNumber()) return

    setIsSearching(true)
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.post(
        `${API_URL}/api/white-listing/async-search`,
        {
          number: phoneNumber,
          countryCode: countryCode,
        },
        config,
      )

      showAlert(data.message, "info")

      setPhoneNumber("")
      fetchSearchResults()
    } catch (error) {
      showAlert(error.response?.data?.message || "Failed to initiate search", "error")
    } finally {
      setIsSearching(false)
    }
  }

  const handleAdd = async () => {
    if (!validatePhoneNumber()) return

    setIsLoading(true)
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.post(`${API_URL}/api/white-listing`, { number: phoneNumber, countryCode }, config)

      showAlert("Number added successfully")
      setPhoneNumber("")

      setNumbers((prevNumbers) => [data, ...prevNumbers])
    } catch (error) {
      showAlert(error.response?.data?.message || "Failed to add number", "error")
    } finally {
      setIsLoading(false)
    }
  }

  const handleSoftDeleteMain = async () => {
    if (!validatePhoneNumber()) return

    setIsLoading(true)
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.put(
        `${API_URL}/api/white-listing/mark-delete`,
        { number: phoneNumber, countryCode },
        config,
      )

      showAlert("Number marked for deletion")
      setPhoneNumber("")

      const existingIndex = numbers.findIndex((n) => n.number === phoneNumber && n.countryCode === countryCode)
      if (existingIndex >= 0) {
        const updatedNumbers = [...numbers]
        updatedNumbers[existingIndex] = data
        setNumbers(updatedNumbers)
      } else {
        setNumbers((prevNumbers) => [data, ...prevNumbers])
      }
    } catch (error) {
      showAlert(error.response?.data?.message || "Failed to mark number for deletion", "error")
    } finally {
      setIsLoading(false)
    }
  }

  const handleSoftDeleteIndividual = async (numberId, number, countryCode) => {
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.put(`${API_URL}/api/white-listing/mark-delete`, { number, countryCode }, config)

      showAlert("Number marked for deletion successfully")

      setNumbers((prevNumbers) =>
        prevNumbers.map((n) => (n._id === numberId ? { ...n, type: data.type, response: data.response } : n)),
      )
      fetchNumbers()
    } catch (error) {
      showAlert(error.response?.data?.message || "Failed to mark number for deletion", "error")
    }
  }

  const handleHardDeleteIndividual = async () => {
    if (!selectedNumberForHardDelete) return

    setIsLoading(true)
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      await axios.delete(`${API_URL}/api/white-listing/${selectedNumberForHardDelete._id}`, config)
      showAlert("Number permanently removed successfully", "success")

      setNumbers((prevNumbers) => prevNumbers.filter((n) => n._id !== selectedNumberForHardDelete._id))
      setSelectedNumberForHardDelete(null)
      setHardDeleteDialogOpen(false)
    } catch (error) {
      showAlert(error.response?.data?.message || "Failed to permanently remove number", "error")
    } finally {
      setIsLoading(false)
    }
  }

  const handleClearAll = async () => {
    setIsLoading(true)
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      await axios.put(`${API_URL}/api/white-listing/clear-all`, {}, config)
      showAlert("All numbers marked for deletion successfully")
      fetchNumbers()
    } catch (error) {
      showAlert(error.response?.data?.message || "Failed to mark all numbers for deletion", "error")
    } finally {
      setIsLoading(false)
      setClearAllDialogOpen(false)
    }
  }

  const handleClearSearchResults = async () => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      await axios.delete(`${API_URL}/api/white-listing/search-results`, config)
      showAlert("Search results cleared successfully")
      setSearchResults([])
    } catch (error) {
      showAlert(error.response?.data?.message || "Failed to clear search results", "error")
    }
  }

  const handleClearIndividualSearchResult = async (searchResultId) => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      await axios.delete(`${API_URL}/api/white-listing/search-results/${searchResultId}`, config)
      showAlert("Search result cleared successfully")

      // Remove from local state
      setSearchResults((prevResults) => prevResults.filter((result) => result._id !== searchResultId))
    } catch (error) {
      showAlert(error.response?.data?.message || "Failed to clear search result", "error")
    }
  }

  const validatePhoneNumber = () => {
    if (!phoneNumber) {
      showAlert("Please enter a phone number", "error")
      return false
    }

    const selectedCountry = countries.find((c) => c.code === country)
    if (!selectedCountry) return false

    if (phoneNumber.length < 5) {
      showAlert("Phone number is too short", "error")
      return false
    }

    return true
  }

  return (
    <DashboardLayout>
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        <Snackbar
          open={alert.open}
          autoHideDuration={6000}
          onClose={handleCloseAlert}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
            {alert.message}
          </Alert>
        </Snackbar>

        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            White Listing Number
          </Typography>

          <Box sx={{ mt: 4, display: "flex", flexWrap: "wrap", gap: 3 }}>
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Choose Option
              </Typography>
              <ToggleButtonGroup value={option} exclusive onChange={handleOptionChange} aria-label="number option">
                <ToggleButton value="Domestic" aria-label="domestic">
                  Domestic
                </ToggleButton>
                <ToggleButton value="International" aria-label="international">
                  International
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box sx={{ minWidth: 200 }}>
              <Typography variant="subtitle1" gutterBottom>
                Choose Country
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={country}
                  onChange={handleCountryChange}
                  displayEmpty
                  renderValue={(selected) => {
                    const selectedCountry = countries.find((c) => c.code === selected)
                    return (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <img
                          loading="lazy"
                          width="20"
                          src={`https://flagcdn.com/w20/${selectedCountry?.code.toLowerCase()}.png`}
                          srcSet={`https://flagcdn.com/w40/${selectedCountry?.code.toLowerCase()}.png 2x`}
                          alt=""
                          style={{ marginRight: 8 }}
                        />
                        {"+"}
                        {selectedCountry?.phone}
                        {"  "}
                        {selectedCountry?.label}
                      </Box>
                    )
                  }}
                >
                  {filteredCountries.map((country) => (
                    <MenuItem key={country.code} value={country.code}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <img
                          loading="lazy"
                          width="20"
                          src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`}
                          srcSet={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png 2x`}
                          alt=""
                          style={{ marginRight: 8 }}
                        />
                        {country.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            <TextField
              fullWidth
              placeholder="Type number here"
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              InputProps={{
                startAdornment: <InputAdornment position="start">{countryCode}</InputAdornment>,
              }}
              disabled={isLoading || isSearching}
            />
          </Box>

          <Box sx={{ mt: 3, display: "flex", justifyContent: "center", gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={isLoading || isSearching}
            >
              {isSearching ? "Searching..." : "Search"}
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} disabled={isLoading || isSearching}>
              {isLoading ? "Adding..." : "Add"}
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleSoftDeleteMain}
              disabled={isLoading || isSearching}
            >
              {isLoading ? "Marking..." : "Mark for Deletion"}
            </Button>
          </Box>

          {isLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h5">Previous</Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchNumbers} disabled={isLoading}>
                Refresh
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteSweepIcon />}
                onClick={handleClearSearchResults}
                disabled={isLoading || searchResults.length === 0}
              >
                Clear All
              </Button>
            </Box>
          </Box>

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>SR. NO.</TableCell>
                    <TableCell>NUMBER</TableCell>
                    <TableCell>SEARCH STATUS</TableCell>
                    <TableCell>RESULT</TableCell>
                    <TableCell>ACTIONS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.length > 0 ? (
                    searchResults.map((result, index) => (
                      <TableRow key={result._id || index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{result.countryCode + result.number}</TableCell>
                        <TableCell>
                          {result.status === "pending" ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <CircularProgress size={16} />
                              Searching...
                            </Box>
                          ) : (
                            "Completed"
                          )}
                        </TableCell>
                        <TableCell>
                          {result.status === "pending" ? (
                            "Processing..."
                          ) : result.response === "Found" ? (
                            <Box sx={{ color: "success.main", fontWeight: "bold" }}>✓ Found in Whitelist</Box>
                          ) : result.response === "Not Found" ? (
                            <Box sx={{ color: "error.main", fontWeight: "bold" }}>✗ Not in Whitelist</Box>
                          ) : (
                            result.response
                          )}
                        </TableCell>
                        <TableCell>
                          {result.status === "completed" && (
                            <Button
                              variant="outlined"
                              color="warning"
                              size="small"
                              startIcon={<Clear />}
                              onClick={() => handleClearIndividualSearchResult(result._id)}
                              sx={{ mr: 1 }}
                            >
                              Clear
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No numbers found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Dialog
          open={clearAllDialogOpen}
          onClose={() => setClearAllDialogOpen(false)}
          aria-labelledby="clear-all-dialog-title"
          aria-describedby="clear-all-dialog-description"
        >
          <DialogTitle id="clear-all-dialog-title">{"Clear All Numbers?"}</DialogTitle>
          <DialogContent>
            <DialogContentText id="clear-all-dialog-description">
              Are you sure you want to clear all white-listed numbers? This action will mark them for deletion and they
              will no longer appear in the list. They will NOT be permanently removed from the database.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setClearAllDialogOpen(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleClearAll} color="error" autoFocus disabled={isLoading}>
              {isLoading ? "Marking..." : "Clear All"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={hardDeleteDialogOpen}
          onClose={() => setHardDeleteDialogOpen(false)}
          aria-labelledby="hard-delete-dialog-title"
          aria-describedby="hard-delete-dialog-description"
        >
          <DialogTitle id="hard-delete-dialog-title">{"Permanently Delete Number?"}</DialogTitle>
          <DialogContent>
            <DialogContentText id="hard-delete-dialog-description">
              Are you sure you want to permanently delete the number{" "}
              <strong>
                {selectedNumberForHardDelete?.countryCode}
                {selectedNumberForHardDelete?.number}
              </strong>
              ? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHardDeleteDialogOpen(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleHardDeleteIndividual} color="error" autoFocus disabled={isLoading}>
              {isLoading ? "Deleting..." : "Permanently Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}

export default WhiteListingPage
